import { useT } from '@/store';
import { eurExact } from '@/lib/format';
import { useClientBalance } from '@/hooks/useClientRequests';

/**
 * V-86. What this client already owes, on the screen where more work is
 * about to be promised to him.
 *
 * core/receivables_reminder.py's own docstring names the decision: "thirty
 * days late is a decision about whether to keep hauling for this client".
 * That decision is taken on two screens — the client-request approval modal
 * and the order form — and until this existed neither had been given a
 * single figure from the money side of the product. Measured on a live
 * server: the receivables endpoint reported UAB Statyba at €8 420,50
 * overdue with the oldest invoice 47 days out and both invoices marked
 * sent, while the payload the approval screen is built from carried 34
 * fields and not one about money.
 *
 * IT NEVER BLOCKS AND NEVER ADVISES. Whether to haul for a late payer is a
 * commercial judgement with reasons the software cannot see — a big client
 * who always pays at sixty days, a relationship worth carrying, a promise
 * made on the phone this morning. The line states a fact and gets out of
 * the way. A product that refused the load would be switched off, and would
 * deserve to be.
 *
 * TWO LINES, BECAUSE THEY ARE TWO ERRANDS. `receivables()` splits the late
 * pile deliberately (see its docstring) and this renders the split rather
 * than the total:
 *
 *  - `dispatch.overdue_sent_*` is late AND in front of the client. That is
 *    the chaseable pile and the only one this may call late, because
 *    telling a dispatcher "this client is 47 days late" about an invoice
 *    nobody ever sent blames the client for the carrier's own filing.
 *  - `dispatch.unsent_*` is money issued and never sent. Its errand is to
 *    send it, not to phone anybody, so it gets its own quieter sentence.
 *
 * Nothing renders when nothing is outstanding. A green "this client is
 * square" on every order form is a line that trains dispatchers to skip the
 * place the red one appears.
 */
export function ClientBalanceNotice({ clientId }) {
  const t = useT();
  const { data } = useClientBalance(clientId);
  if (!data) return null;

  const chaseable = data.dispatch?.overdue_sent_eur ?? 0;
  const chaseableDays = data.dispatch?.oldest_overdue_sent_days ?? 0;
  const unsent = data.dispatch?.unsent_eur ?? 0;
  const unsentCount = data.dispatch?.unsent_count ?? 0;
  if (chaseable <= 0 && unsent <= 0) return null;

  return (
    <div role="note" className="rounded-[10px] border border-amber/40 bg-amber/10 p-2.5">
      {chaseable > 0 ? (
        <p className="text-[11.5px] font-semibold text-amber">
          {t('clients.balanceOverdue', {
            eur: eurExact(chaseable),
            days: chaseableDays,
            n: data.dispatch.overdue_sent_count,
          })}
        </p>
      ) : null}
      {unsent > 0 ? (
        <p className="mt-0.5 text-[10.5px] text-dim">
          {t('clients.balanceUnsent', { eur: eurExact(unsent), n: unsentCount })}
        </p>
      ) : null}
      {/* The same disclosure the receivables panel makes, and for the same
          reason: nothing here reconciles against a bank, so "unpaid" rests
          entirely on nobody having marked it paid by hand. A dispatcher
          about to have an awkward conversation is owed that caveat. */}
      <p className="mt-1 text-[10px] leading-snug text-dim">
        {t('clients.balanceManual')}
      </p>
    </div>
  );
}
